import * as THREE from 'three';
import { SketchPlanarFaceResolver } from '../../../sketch/face/SketchPlanarFaceResolver';
import { resolveModelTopologyOwnerResultMesh } from '../../../utils/feature/resolveModelTopologyOwnerResultMesh';
import type {
    ExtrudeSessionSelection,
    ExtrudeSessionTarget,
} from './ExtrudeFeatureSessionBridge';

/**
 * 拉伸面选择目标解析输入。
 */
export interface ExtrudeFaceSelectionTargetResolverInput {
    /** 当前场景。 */
    readonly scene: THREE.Scene | null;
    /** 被选中的结果体或导入 body owner ID。 */
    readonly objectId: string;
    /** 被选中的面索引。 */
    readonly faceIndex: number;
    /** 选择来源类型。 */
    readonly sourceType: 'face' | 'solid_face';
    /** Viewer 提供的对象解析入口。 */
    readonly getSceneObjectById?: (objectId: string) => THREE.Object3D | null | undefined;
}

/**
 * 拉伸面选择目标解析结果。
 */
export interface ExtrudeFaceSelectionTargetResolverResult {
    /** 拉伸会话目标。 */
    readonly target: ExtrudeFaceSelectionTarget;
    /** 可序列化的高层面选择。 */
    readonly selection: Extract<ExtrudeSessionSelection, { kind: 'face' }>;
}

/**
 * 带高层选择快照的拉伸面目标。
 */
export interface ExtrudeFaceSelectionTarget extends ExtrudeSessionTarget {
    /** 当前面选择快照。 */
    readonly selection: Extract<ExtrudeSessionSelection, { kind: 'face' }>;
}

/**
 * 拉伸面选择目标解析器。
 *
 * 设计说明：
 * 1. 面选择到拉伸目标的转换属于 CAD 拓扑语义，不应由 web-client 直接读取 THREE.Mesh；
 * 2. 手动结果体和导入 body 都先统一解析为 FeatureResultMesh，再用 FeatureFace 的平面快照构建目标；
 * 3. 返回值保持现有 extrude session 协议，避免 UI 层关心导入体、手动体的差异。
 */
export class ExtrudeFaceSelectionTargetResolver {
    /** 平面面解析器。 */
    private readonly planarFaceResolver = new SketchPlanarFaceResolver();

    /**
     * 从面选择解析拉伸目标。
     *
     * @param input 解析输入。
     * @returns 拉伸目标；无法解析或非平面时返回 `null`。
     */
    public resolve(input: ExtrudeFaceSelectionTargetResolverInput): ExtrudeFaceSelectionTargetResolverResult | null {
        const resultMesh = resolveModelTopologyOwnerResultMesh({
            scene: input.scene,
            ownerObjectId: input.objectId,
            getSceneObjectById: input.getSceneObjectById,
        });
        const featureFace = resultMesh?.getFeatureFaceByIndex(input.faceIndex) ?? null;
        if (!resultMesh || !featureFace) {
            return null;
        }

        const faceSelection = this.planarFaceResolver.resolveFromSelectionObject(featureFace);
        if (!faceSelection?.isPlanar) {
            return null;
        }

        const normal = this.resolveOutwardNormal({
            resultMesh,
            facePoint: faceSelection.worldRepresentativePoint,
            faceNormal: faceSelection.worldPlaneNormal,
        });
        const normalHint: [number, number, number] = [normal.x, normal.y, normal.z];
        const selection: Extract<ExtrudeSessionSelection, { kind: 'face' }> = {
            kind: 'face',
            objectId: input.objectId,
            faceIndex: input.faceIndex,
            sourceType: input.sourceType,
            normalHint,
        };

        return {
            selection,
            target: {
                params: input.sourceType === 'face'
                    ? {
                        sourceType: 'face',
                        faceId: input.objectId,
                        normalHint,
                    }
                    : {
                        sourceType: 'solid_face',
                        solidId: input.objectId,
                        faceIndex: input.faceIndex,
                        normalHint,
                },
                selection,
                normal,
                basePoint: faceSelection.worldRepresentativePoint.clone(),
                sourceMeshId: input.objectId,
            },
        };
    }

    /**
     * 解析实体面外法向。
     *
     * @param input 法向解析输入。
     * @returns 已归一化的外法向。
     */
    private resolveOutwardNormal(input: {
        readonly resultMesh: THREE.Object3D;
        readonly facePoint: THREE.Vector3;
        readonly faceNormal: THREE.Vector3;
    }): THREE.Vector3 {
        const normal = input.faceNormal.clone().normalize();
        const bounds = new THREE.Box3().setFromObject(input.resultMesh);
        if (bounds.isEmpty()) {
            return normal;
        }

        const solidCenter = bounds.getCenter(new THREE.Vector3());
        const outwardDirection = input.facePoint.clone().sub(solidCenter);
        if (outwardDirection.lengthSq() > 1e-12 && outwardDirection.dot(normal) < 0) {
            return normal.multiplyScalar(-1);
        }
        return normal;
    }
}
